import { action, computed, observable, runInAction } from 'mobx'
import { JITSI_TIPS_CHECK_INTERVAL } from '@/constants/jitsi.js'
import Scheduler from '@/utils/Scheduler.js'
import { getToastIconComponent } from '@/components/ui/sonner.jsx'
import { mdiCurrencyUsd } from '@mdi/js'
import { usdFormatter } from '@/utils/format.js'

export default class JitsiIframeApiStore {
  #jitsi = null
  #checkTipsScheduler = null

  @observable accessor #client = null
  @observable accessor id = ''
  @observable accessor isModerator = false

  constructor(jitsiStore) {
    this.#jitsi = jitsiStore
    this.#checkTipsScheduler = new Scheduler(this.#checkTips, JITSI_TIPS_CHECK_INTERVAL, false)
  }

  deconstructor = () => {
    this.#checkTipsScheduler.deconstructor()
  }

  @action
  init = jitsiApi => {
    this.reset()

    this.#client = jitsiApi

    this.#client.addListener('videoConferenceJoined', this.#onConnected)
    this.#client.addListener('videoConferenceLeft', this.#jitsi.onDisconnected)
    this.#client.addListener('participantJoined', this.#onUserJoined)
    this.#client.addListener('participantLeft', this.#onUserLeft)
    this.#client.addListener('displayNameChange', this.#onDisplayNameChange)
    this.#client.addListener('participantRoleChanged', this.#onUserRoleChanged)
    this.#client.addListener('errorOccurred', this.#jitsi.onError)

    console.log('[Jitsi] Iframe API initialized')
  }

  @action
  reset = () => {
    if (!this.#client) return

    this.#checkTipsScheduler.reset()

    this.#client.executeCommand('hangup')

    this.#client.removeListener('videoConferenceJoined', this.#onConnected)
    this.#client.removeListener('videoConferenceLeft', this.#jitsi.onDisconnected)
    this.#client.removeListener('participantJoined', this.#onUserJoined)
    this.#client.removeListener('participantLeft', this.#onUserLeft)
    this.#client.removeListener('displayNameChange', this.#onDisplayNameChange)
    this.#client.removeListener('participantRoleChanged', this.#onUserRoleChanged)
    this.#client.removeListener('errorOccurred', this.#jitsi.onError)

    this.#client.dispose()
    this.#client = null

    this.id = ''
    this.isModerator = false
  }

  get api() {
    return this.#jitsi.rootStore.apiStore
  }

  get auth() {
    return this.#jitsi.rootStore.authStore
  }

  get wallet() {
    return this.#jitsi.rootStore.walletStore
  }

  @computed
  get isConnected() {
    return Boolean(this.#client)
  }

  endConference = () => {
    if (this.isModerator) this.#client.executeCommand('endConference')
  }

  syncLocalUserData = () => {
    this.#client.executeCommand('displayName', this.auth.user.nickname)
    if (this.auth.user.hasStore) this.#client.executeCommand('avatarUrl', this.auth.user.avatar)
  }

  #onConnected = async ({ id }) => {
    const currentRoom = (await this.#client.getRoomsInfo()).rooms.find(x => x.isMainRoom)

    runInAction(() => {
      this.id = id
      this.isModerator = currentRoom.participants.find(x => x.id === id)?.role === 'moderator'
    })

    this.syncLocalUserData()
    if (this.isModerator) this.#client.executeCommand('subject', this.#jitsi.post.title)

    currentRoom.participants.forEach(x => {
      if (x.id !== this.id) this.#jitsi.setUser(x.id, x.displayName)
    })

    this.#jitsi.onConnected()
    this.#jitsi.onRoomJoined()

    if (this.#jitsi.post.user.isAuthUser) {
      await this.api.setJitsiOwner(this.#jitsi.post.id, id)
      this.#client.executeCommand('setFollowMe', true)

      if (this.#jitsi.post.isLivestreamViaJitsi) {
        this.#client.executeCommand('setLargeVideoParticipant', id)
        this.#client.executeCommand('pinParticipant', id)
      }
    }

    if (this.#jitsi.post.isMeeting || this.#jitsi.post.isLivestream)
      this.#checkTipsScheduler.start(true).then()

    if (this.#jitsi.isPurchase && this.#jitsi.post.isOneTimePayment) this.wallet.updateAll().then()
  }

  #onUserJoined = ({ id, displayName }) => {
    this.#ensurePostOwnerIsModerator()
    this.#jitsi.onUserJoined(id, displayName)
  }

  #onUserLeft = ({ id }) => {
    this.#jitsi.onUserLeft(id)
  }

  #onDisplayNameChange = event => {
    this.#jitsi.setUser(event.id, event.displayname ?? event.displayName, true)
  }

  @action
  #onUserRoleChanged = ({ id, role }) => {
    if (id === this.id) this.isModerator = role === 'moderator'
    this.#ensurePostOwnerIsModerator()
  }

  #ensurePostOwnerIsModerator = () => {
    if (this.isModerator && !this.#jitsi.post.user.isAuthUser)
      this.#client.executeCommand('grantModerator', this.#jitsi.hostUser.jitsiId)
  }

  #checkTips = async () => {
    const response = await this.api.getNotifications()
    if (!response?.data?.messagesJson) return

    // NOT USED: x.fromPicture, x.toPicture
    response.data.messages.forEach(x => {
      const amountFormatted = usdFormatter.format(x.amount)

      this.#jitsi.notifications.toast(
        `${x.fromNickname} tipped ${x.toNickname} ${amountFormatted}`,
        {
          icon: getToastIconComponent(mdiCurrencyUsd),
        }
      )
    })
  }
}
